import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/app/providers/AuthProvider';
import { AppLayout } from '@/app/layout/AppLayout';
import { ForcePasswordChangePage } from '@/features/auth/ForcePasswordChangePage';
import { SkeletonListPage } from '@/ui';
import styles from './guards.module.css';

function initialsFor(fullName: string | null, username: string): string {
  const source = fullName?.trim() !== '' && fullName !== null ? fullName : username;
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const second = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return (first + second).toUpperCase() || '?';
}

/**
 * The placeholder shown while the session is being restored.
 *
 * It is the SHAPE of the app, not a spinner. A spinner here meant a refresh
 * showed two different loading screens back to back: a centred spinner on an
 * empty canvas, then the whole shell with skeleton content. Using the same
 * placeholder the route boundary uses makes it one continuous state, and the
 * real shell lands on a layout that already matches it.
 */
function SessionLoading() {
  return (
    <div className={styles.loading} role="status" aria-label="Restoring your session">
      <div className={styles.loadingSidebar} />
      <div className={styles.loadingContent}>
        <SkeletonListPage />
      </div>
    </div>
  );
}

/**
 * Gate for every signed-in route.
 *
 * Three things happen here, in order: wait for the session check, bounce to sign-in
 * while remembering where the user was headed, and hold them on the password screen
 * while `must_change_password` is set — a gate the backend does not enforce.
 */
export function RequireAuth() {
  const { status, user, mustChangePassword, isAdmin } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <SessionLoading />;

  if (status === 'anonymous' || user === null) {
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }

  if (mustChangePassword) return <ForcePasswordChangePage />;

  return (
    <AppLayout
      isAdmin={isAdmin}
      user={{
        name: user.fullName ?? user.username,
        initials: initialsFor(user.fullName, user.username),
        role: user.role === 'superadmin' ? 'Super admin' : user.role.replace(/^./, (c) => c.toUpperCase()),
      }}
    />
  );
}

/**
 * Gate for the 52 admin-only routes.
 *
 * Renders a real 403 rather than redirecting. The legacy app simply did not register
 * admin routes for non-admins, so a shared link silently landed on the dashboard and
 * the user never learned why.
 */
export function RequireAdmin() {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to="/forbidden" replace />;
  return <Outlet />;
}
